// src/hooks/useWireRebuild.js
//
// JBZ load sonrası wireSlice'taki polyline'ları sahneye geri çizer.
// Tesisat rebuild ile aynı pattern: Redux veri katmanı → factory → mesh.
//
// Three.js Line/Group objeleri JSON round-trip'te güvenilir restore edilemediği
// için (geometry attributes, material durumları, parent-child referansları),
// vertices + color + userData kullanılarak sıfırdan inşa edilir.

import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as THREE from 'three';
import { selectAllWires } from '../../../redux/wireSlice';
import { findFloorPlanMeshById } from '../../../modules/fpSync';
import { flog } from '../../../utils/forensics'; // GECICI — forensics bitince silinecek
import { shouldSkipForSnapshot } from '../sceneSnapshotMode';

function parseColor(c) {
  if (typeof c === 'number') return c;
  if (typeof c === 'string') {
    // '#rrggbb' veya 'rrggbb'
    const hex = c.startsWith('#') ? c.slice(1) : c;
    const num = parseInt(hex, 16);
    return Number.isFinite(num) ? num : 0xffffff;
  }
  return 0xffffff;
}

function buildWire(item) {
  if (!item) return null;
  const verts = Array.isArray(item.vertices) ? item.vertices : null;
  if (!verts || verts.length < 2) return null;

  const points = verts
    .map(v => new THREE.Vector3(v?.x ?? 0, v?.y ?? 0, v?.z ?? 0))
    .filter(v => Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z));
  if (points.length < 2) return null;

  // BUG-B/E FIX: curvedLine tipindeki wire'lar spline olarak cizilmeli.
  // ✅ İSTİSNA: lineMode (L tuşu ile düz çizilen armatür hattı) spline'a çevrilmez.
  let geometry;
  if (item.type === 'curvedLine' && !item.userData?.lineMode && points.length >= 2) {
    const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.5);
    const divisions = Math.max(points.length * 8, 32);
    geometry = new THREE.BufferGeometry().setFromPoints(curve.getPoints(divisions));
  } else {
    geometry = new THREE.BufferGeometry().setFromPoints(points);
  }
  const material = new THREE.LineBasicMaterial({ color: parseColor(item.color) });
  const line = new THREE.Line(geometry, material);
  // Mesh uuid'sini kayıttaki sceneUUID'ye eşitle: snap sistemi wire mesh'ini Redux
  // wire'ın sceneUUID'si ile getObjectByProperty('uuid', ...) yapıp buluyor. Eşitlenmezse
  // rebuild rastgele uuid alır → snap wire'ı bulamaz → wire üzerinde klavuz çizgileri yok.
  if (item.sceneUUID) line.uuid = item.sceneUUID;
  line.name = 'wire';
  line.userData = {
    ...(item.userData || {}),
    id: item.id,
    type: 'wire',
    layer: item.layer || '0',
    isWireRebuilt: true,
  };
  return line;
}

function findInScene(scene, id) {
  let found = null;
  scene.traverse(obj => {
    // Sadece id eşleşmesi — interactive akıştaki wire mesh'leri
    // 'wireMachine', 'wireSocket', 'feederWire', 'polyline' gibi farklı
    // type'larla yaratılıyor. type='wire' filtresi koyulursa bunlar
    // bulunamaz, useWireRebuild duplicate Line mesh ekler → sahnede aynı
    // id'ye sahip iki wire mesh (görsel "yeni exit'ten de çıkıyor" bug'ı).
    //
    // useSocket/useLighting interactive line.userData.id = wireID set ediyor;
    // Redux'a ise addWire({id: feederId, ...}) dispatch ediliyor (iki ayrı
    // entity ID'si: wire mesh ID'si ile feeder kaydının ID'si). Bağlantı
    // line.userData.feederId'de tutuluyor. useWireRebuild item.id=feederId
    // ile arama yaparsa, userData.feederId üzerinden de eşleşmesi şart;
    // yoksa interactive wire'ı bulamayıp her addWire'da rebuild kopya ekler
    // → çift hat (kullanıcı: ikinci hat eklenince ilk hat duplicate görünüyor).
    if (!found && (obj.userData?.id === id || obj.userData?.feederId === id)) found = obj;
  });
  return found;
}

function disposeMesh(obj) {
  if (!obj) return;
  obj.traverse?.(child => {
    if (child.geometry) child.geometry.dispose();
    if (child.material) {
      if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
      else child.material.dispose();
    }
  });
  if (obj.parent) obj.parent.remove(obj);
}

// floorPlanSlice legacy key → resolved key mapping
const RESOLVED_TO_LEGACY = {
  strong_current: 'elektrik',
  weak_current: 'telekom',
  fire_detection: 'yangin',
  construction_site: 'santiye',
  connection_line: 'baglanti',
};

export default function useWireRebuild(scenesRef, tabId) {
  const wires = useSelector(selectAllWires);
  // Tab'in floor plan verileri — filtreleme ve sourceFloorPlanId eşlemesi için
  const legacyTabId = RESOLVED_TO_LEGACY[tabId] || tabId;
  const tabFpAllIds = useSelector(state => state.floorPlans.tabs?.[legacyTabId]?.allIds);
  const tabFpById = useSelector(state => state.floorPlans.tabs?.[legacyTabId]?.byId);
  const builtRef = useRef(new Map()); // id → mesh
  const lastSnapshotSkippedRef = useRef(0);

  useEffect(() => {
    builtRef.current = new Map();
  }, [tabId]);

  useEffect(() => {
    const scene = scenesRef?.current?.[tabId];
    if (!scene) return;

    // [FAZ 2] Snapshot mode: scene snapshot'tan yüklendi, wire'lar zaten içinde
    if (shouldSkipForSnapshot(lastSnapshotSkippedRef)) {
      scene.traverse(o => {
        if (o.userData?.id && (o.userData?.type || o.isLine)) {
          builtRef.current.set(o.userData.id, o);
        }
      });
      return;
    }

    // ─── Cleanup: önceki bug'dan kaynaklanan duplicate rebuilt mesh'leri sil ──
    // Eski findInScene type='wire' filtresi yüzünden, interactive akıştaki wire
    // mesh'i (type='wireMachine'/'polyline'/...) bulunamayıp her addWire'da
    // ek bir rebuilt mesh (type='wire', isWireRebuilt=true) ekleniyordu.
    // Aynı id'ye sahip birden fazla mesh varsa rebuilt olanı dispose et.
    const meshesById = new Map();
    scene.traverse(obj => {
      const id = obj.userData?.id;
      if (!id) return;
      if (!(obj.userData?.isWireRebuilt) && obj.type !== 'Line') return;
      if (!meshesById.has(id)) meshesById.set(id, []);
      meshesById.get(id).push(obj);
    });
    for (const [, group] of meshesById) {
      if (group.length < 2) continue;
      // Eğer aynı id'ye sahip birden fazla mesh varsa, rebuilt olanları dispose et
      group.forEach(m => { if (m.userData?.isWireRebuilt) disposeMesh(m); });
    }

    const currentIds = new Set();
    // Tab FP ID seti + mimari source ID'leri
    const tabFpIdSet = new Set();
    if (tabFpAllIds && tabFpById) {
      for (const fpId of tabFpAllIds) {
        tabFpIdSet.add(fpId);
        const plan = tabFpById[fpId];
        if (plan?.sourceFloorPlanId) tabFpIdSet.add(plan.sourceFloorPlanId);
      }
    }

    for (const item of wires) {
      if (!item?.id) continue;
      // DXF kaynaklı objeler wire DEĞİLDİR. Eski save bug'ı (isWireLike DXF
      // polyline'larını wire sanıyordu) yüzünden wires.json'a yazılmış DXF
      // çizgilerini elektrik sahnesine DÖKME — "489 mimari çöp" tam buydu.
      // DXF kendi yolundan (dxf-data.json / floor-plans.json) restore edilir.
      if (item.userData?.source === 'dxf') continue;
      // Scene routing: sadece bu tab'ın floor plan'larına ait wire'ları rebuild et
      const wireFpId = item.userData?.floorPlanId || item.floorPlanId;
      if (wireFpId && tabFpIdSet.size > 0 && !tabFpIdSet.has(wireFpId)) continue;
      currentIds.add(item.id);

      // Sahnede zaten var mı? (interactive ekleme veya önceki rebuild)
      const existing = findInScene(scene, item.id);
      if (existing) {
        builtRef.current.set(item.id, existing);
        continue;
      }

      const mesh = buildWire(item);
      if (mesh) {
        if (item._v === 3 && wireFpId) {
          // _v:3: vertex'ler fpMesh-LOCAL — dogrudan fpMesh'e ekle
          const resolvedFpId = (tabFpById && Object.values(tabFpById).find(p => p?.sourceFloorPlanId === wireFpId)?.id) || wireFpId;
          const fpMesh = findFloorPlanMeshById(scene, resolvedFpId);
          if (fpMesh) {
            fpMesh.add(mesh);
          } else {
            // fpMesh yok — sonraki run'da tekrar denenecek
            continue;
          }
        } else {
          scene.add(mesh);
        }
        builtRef.current.set(item.id, mesh);
        const verts = item.vertices || [];
        flog('FAZ16_WIRE_EKLENDI', {
          id: (item.id || '').slice(-8),
          type: item.type || 'wire',
          wireType: item.wireType,
          vertexCount: verts.length,
          firstVertex: verts[0] ? { x: verts[0].x?.toFixed(1), y: verts[0].y?.toFixed(1) } : null,
          lastVertex: verts.length > 0 ? { x: verts[verts.length - 1].x?.toFixed(1), y: verts[verts.length - 1].y?.toFixed(1) } : null,
          floorPlanId: item.userData?.floorPlanId || item.floorPlanId,
        });
      } else {
        flog('FAZ16_WIRE_BASARISIZ', {
          id: (item.id || '').slice(-8),
          hasVertices: !!item.vertices,
          vertexCount: item.vertices?.length,
        });
      }
    }

    // Redux'tan silinenleri sahneden çıkar (sadece wire-rebuilt olanlar)
    for (const [id, mesh] of Array.from(builtRef.current.entries())) {
      if (!currentIds.has(id)) {
        if (mesh.userData?.isWireRebuilt) disposeMesh(mesh);
        builtRef.current.delete(id);
      }
    }
  }, [wires, tabId, tabFpAllIds, tabFpById, scenesRef]);
}
